import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Play, Sparkles } from 'lucide-react';
import { artists, newsArticles } from '../data/mock';
import { Button } from '../components/ui/button';

export const Home = () => {
  const videoRef = useRef(null);
  const artistsScrollRef = useRef(null);

  useEffect(() => {
    // Smooth scroll animation for artists section
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('.fade-in-section');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []); // Empty deps is correct - this runs once on mount to set up observers

  const featuredArtists = artists.filter(a => a.isRevealed).slice(0, 6);

  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section with Video Background */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Video Background */}
        <div className="absolute inset-0 z-0">
          <video
            ref={videoRef}
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover opacity-25"
          >
            <source src="https://customer-assets.emergentagent.com/job_bekar-360-platform/artifacts/q9ucz9fl_6337945_Vinyl%20Player%20Music%20Record%20Player_By_BublikHaus_Artlist_HD.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 container mx-auto px-6 text-center">
          <div className="max-w-5xl mx-auto fade-in-section">
            <div className="flex items-center justify-center gap-2 mb-6">
              <Sparkles className="text-white w-5 h-5" />
              <span className="text-white/80 text-sm tracking-widest uppercase">
                360° Music Ecosystem
              </span>
            </div>
            <h1 className="text-6xl md:text-8xl font-bold text-white mb-6 tracking-tight leading-tight">
              Where Artists
              <br />
              <span className="bg-gradient-to-r from-white via-white/90 to-white/70 bg-clip-text text-transparent">
                Take Flight
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-white/70 mb-12 max-w-3xl mx-auto leading-relaxed">
              From production to world stages, we provide everything artists need to soar. 
              A complete ecosystem for the modern music era.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link to="/contact">
                <Button 
                  size="lg" 
                  className="bg-white text-black hover:bg-white/90 text-base px-8 py-6 rounded-full transition-all duration-300 hover:scale-105 shadow-2xl shadow-white/20"
                >
                  Join Our Roster
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/ecosystem">
                <Button 
                  size="lg" 
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-black text-base px-8 py-6 rounded-full transition-all duration-300 hover:scale-105"
                >
                  Explore The Ecosystem
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
            <div className="w-1 h-3 bg-white/50 rounded-full" />
          </div>
        </div>
      </section>

      {/* BEKÂR Values Section - Inspired by AWAL */}
      <section className="py-32 bg-black fade-in-section">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            {/* Big Impactful Statements - Same size as hero */}
            <div className="text-center mb-20">
              <h2 className="text-6xl md:text-8xl font-bold text-white leading-tight mb-6">
                One <span style={{ color: '#00A672' }}>Vision</span>.
              </h2>
              <h2 className="text-6xl md:text-8xl font-bold text-white leading-tight mb-6">
                Total <span style={{ color: '#00A672' }}>Alignment</span>.
              </h2>
              <h2 className="text-6xl md:text-8xl font-bold text-white leading-tight">
                Long-Term <span style={{ color: '#00A672' }}>Career</span>.
              </h2>
            </div>

            {/* Supporting Points - Centered with staggered animation */}
            <div className="space-y-6 mb-20 text-center">
              <div 
                className="text-white/80 text-xl md:text-2xl font-light fade-in-stagger"
                style={{ animationDelay: '0.1s' }}
              >
                Your vision. Your rights.
              </div>
              <div 
                className="text-white/80 text-xl md:text-2xl font-light fade-in-stagger"
                style={{ animationDelay: '0.2s' }}
              >
                Your revenue. Your rules.
              </div>
              <div 
                className="text-white/80 text-xl md:text-2xl font-light fade-in-stagger"
                style={{ animationDelay: '0.3s' }}
              >
                No blind spots.
              </div>
              <div 
                className="text-white/80 text-xl md:text-2xl font-light fade-in-stagger"
                style={{ animationDelay: '0.4s' }}
              >
                Freedom to walk anytime.
              </div>
              <div 
                className="text-white/80 text-xl md:text-2xl font-light fade-in-stagger"
                style={{ animationDelay: '0.5s' }}
              >
                Final say, always.
              </div>
            </div>

            {/* CTA */}
            <div className="text-center">
              <Link to="/contact">
                <Button 
                  size="lg"
                  className="bg-white text-black hover:bg-white/90 text-xl px-12 py-8 rounded-full transition-all duration-300 hover:scale-105 shadow-2xl shadow-white/20"
                >
                  Build with Us
                  <ArrowRight className="ml-3 w-6 h-6" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Artists Section */}
      <section className="py-24 bg-gradient-to-b from-black to-zinc-950 fade-in-section">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-bold text-white mb-4">
              Featured Artists
            </h2>
            <p className="text-xl text-white/60 max-w-2xl mx-auto">
              Discover the voices shaping the future of music
            </p>
          </div>

          {/* Artist Cards Scroll */}
          <div 
            ref={artistsScrollRef}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12"
          >
            {featuredArtists.map((artist, index) => (
              <Link
                key={artist.id}
                to="/artists"
                className="group relative overflow-hidden rounded-2xl aspect-square"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <img 
                  src={artist.image} 
                  alt={artist.name}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent opacity-80 group-hover:opacity-90 transition-opacity duration-300" />
                <div className="absolute inset-0 flex flex-col justify-end p-8">
                  <p className="text-white/70 text-sm mb-2 tracking-wide">{artist.genre}</p>
                  <h3 className="text-3xl font-bold text-white mb-2 transition-transform duration-300 group-hover:translate-x-2">
                    {artist.name}
                  </h3>
                  <div className="flex items-center text-white/60 group-hover:text-white transition-colors duration-300">
                    <Play size={16} className="mr-2" />
                    <span className="text-sm">View Artist</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center">
            <Link to="/artists">
              <Button 
                size="lg"
                variant="outline"
                className="border-2 border-white/20 text-white hover:bg-white hover:text-black rounded-full px-8 transition-all duration-300 hover:scale-105"
              >
                Explore All Artists
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Eco-System Teaser */}
      <section className="relative py-24 overflow-hidden fade-in-section">
        {/* Video Background */}
        <div className="absolute inset-0 z-0">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover opacity-25"
          >
            <source src="https://customer-assets.emergentagent.com/job_bekar-360-platform/artifacts/zxi5a0yh_EcoSystem.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black" />
        </div>

        {/* Content */}
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-5xl md:text-6xl font-bold text-white mb-6">
              The Complete
              <span className="block mt-2 bg-gradient-to-r from-white via-white/90 to-white/70 bg-clip-text text-transparent">
                360° Ecosystem
              </span>
            </h2>
            <p className="text-xl text-white/70 mb-12 leading-relaxed">
              Six integrated services working in harmony to elevate your career. 
              From the studio to the stage, we've got you covered.
            </p>
            <Link to="/ecosystem">
              <Button 
                size="lg"
                className="bg-white text-black hover:bg-white/90 text-base px-8 py-6 rounded-full transition-all duration-300 hover:scale-105 shadow-2xl shadow-white/10"
              >
                Discover How It Works
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Latest News */}
      <section className="py-24 bg-gradient-to-b from-zinc-950 to-black fade-in-section">
        <div className="container mx-auto px-6">
          <div className="flex justify-between items-end mb-16">
            <div>
              <h2 className="text-5xl md:text-6xl font-bold text-white mb-4">
                Latest News
              </h2>
              <p className="text-xl text-white/60">
                Stay updated with our journey
              </p>
            </div>
            <Link to="/news">
              <Button 
                variant="ghost"
                className="text-white hover:bg-white/10 hidden md:flex"
              >
                View All News
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {newsArticles.map((article, index) => (
              <Link
                key={article.id}
                to="/news"
                className="group"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="relative overflow-hidden rounded-xl aspect-square mb-4">
                  <img 
                    src={article.image} 
                    alt={article.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                  <div className="absolute top-4 left-4">
                    <span className="text-xs text-white/80 bg-white/10 backdrop-blur-md px-3 py-1 rounded-full">
                      {article.category}
                    </span>
                  </div>
                </div>
                <p className="text-white/50 text-sm mb-2">{article.date}</p>
                <h3 className="text-white text-lg font-semibold leading-tight group-hover:text-white/80 transition-colors duration-300">
                  {article.title}
                </h3>
              </Link>
            ))}
          </div>

          <div className="text-center mt-12 md:hidden">
            <Link to="/news">
              <Button 
                variant="outline"
                className="border-2 border-white/20 text-white hover:bg-white hover:text-black rounded-full"
              >
                View All News
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
